<script lang="ts">
  import type { FileSystem, Folder, NodeId, BindId, Node, EmbodiedEntry } from "../lib/filesystem/fileSystem";
  import FileManagerFile from "./FileManagerFile.svelte";
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { fileManagerDragging, newFile, type Dragging, getCurrentDateTime, fileManagerUsedSizeToken, copyBookOrFolderInterFileSystem, saveBookTo, exportFolderAsEnvelopeZip, importEnvelopeZipToFolder, loadBookFrom, selectedEntries, lastSelectedEntry, handleEntryClick, buildDragging, removeSelectedEntries, renameSelectedEntries, exportSelectedEntries, type BulkRenameResult, folderCollapsedState } from "./fileManagerStore";
  import { waitDialog } from '../utils/waitDialog';
  import { readEnvelope, readOldEnvelope } from "../lib/book/envelope";
  import { newBook } from "../lib/book/book";
  import { mainBook, mainBookTitle } from '../bookeditor/workspaceStore';
  import FileManagerFolderTail from "./FileManagerFolderTail.svelte";
  import FileManagerInsertZone from "./FileManagerInsertZone.svelte";
  import RenameEdit from "../utils/RenameEdit.svelte";
  import { toolTip } from '../utils/passiveToolTipStore';
  import { loading, progress } from '../utils/loadingStore'
  import { collectGarbage, purgeCollectedGarbage } from "../utils/garbageCollection";
  import { toastStore } from '@skeletonlabs/skeleton';

  import newFileIcon from '../assets/fileManager/new-file.webp';
  import newFolderIcon from '../assets/fileManager/new-folder.webp';
  import trashIcon from '../assets/fileManager/trash.webp';
  import folderIcon from '../assets/fileManager/folder.webp';
  import renameIcon from '../assets/fileManager/rename.webp';
  import packageExportIcon from '../assets/fileManager/package-export.webp';
  import mergeIcon from '../assets/fileManager/package-import.webp';

  export let fileSystem: FileSystem;
  export let filename: string;
  export let bindId: BindId;
  export let parent: Folder;
  export let trash: Folder | null; // 捨てるときの対象、ごみ箱自身はnull
  export let removability: "removable" | "unremovable" = "removable";
  export let spawnability: "file-spawnable" | "folder-spawnable" | "unspawnable" = "unspawnable";
  export let index: number;
  export let path: string[];

  let node: Folder;
  let acceptable: boolean;
  let isDraggingOver: boolean;
  let isDiscardable = false;
  let renameEdit: RenameEdit;
  let renaming = false;
  let isRootTrash = false;
  let embodiedEntries: EmbodiedEntry[] = [];
  let collapsed = removability === "removable";
  let folderDropZoneDraggingOver = false;

  const dispatch = createEventDispatcher();

  $: if (node) { folderCollapsedState.set(node.id, collapsed); }

  $: updateEntries(node);
  async function updateEntries(node: Folder) {
    if (node) {
      embodiedEntries = await node.listEmbodied();
    }
  }

  $: ondrag($fileManagerDragging);
  function ondrag(dragging: Dragging | null) {
    acceptable = dragging != null && !dragging.entries.some(e => path.includes(e.bindId));
  }

  async function onDragOver(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    isDraggingOver = true;
  }

  function onDragLeave() {
    isDraggingOver = false;
  }

  async function onDropHere(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    if (acceptable) {
      console.log(ev);
      await moveToHere(null);
      collapsed = false;
    } else {
      // jsonだったら、jsonの中身を見て、適切な処理をする
      if (ev.dataTransfer?.files.length === 1) {
        const file = ev.dataTransfer.files[0];
        const fileName = file.name;
        if (file.type === "application/json") {
          // 旧タイプのenvelope
          try {
            $loading = true;
            const json = await file.text();
            const book = await readOldEnvelope(json);
            const newFile = await fileSystem.createFile();
            await saveBookTo(book, fileSystem, newFile);
            await node.link("パッケージ", newFile.id);
            node = node;
            collapsed = false;
          }
          finally {
            $loading = false;
          }
          return;
        }
        if (fileName.endsWith(".envelope")) {
          console.log("loading envelope")
          // 新タイプのenvelope
          try {
            $progress = 0;
            const book = await readEnvelope(file, n => $progress = n);
            const newFile = await fileSystem.createFile();
            book.revision.id = newFile.id;
            $progress = null;
            $loading = true;
            await saveBookTo(book, fileSystem, newFile);
            const basename = fileName.replace(/\.envelope$/, "");
            console.log("importing envelope done")
            await node.link(basename, newFile.id);
            node = node;
            collapsed = false;
            toastStore.trigger({ message: $_('fileManager.envelopeImported'), timeout: 2000});
          }
          finally {
            $progress = null;
            $loading = false;
          }
          return;
        }
        
        if (fileName.endsWith(".zip")) {
          // ZIPファイル（階層型envelope）
          try {
            $progress = 0;
            $loading = true;
            
            // ZIPファイルの読み込み
            await importEnvelopeZipToFolder(
              fileSystem,
              node,
              file,
              n => $progress = n
            );
            
            // 更新を反映
            node = node;
            collapsed = false;
            toastStore.trigger({ message: $_('fileManager.folderImported'), timeout: 2000});
          }
          catch (error) {
            console.error('ZIPファイルのインポート中にエラーが発生しました:', error);
            toastStore.trigger({ message: $_('fileManager.zipImportFailed'), timeout: 3000});
          }
          finally {
            $progress = null;
            $loading = false;
          }
          return;
        }
      }
      console.log("not acceptable");
    }
  }

  async function addFolder() {
    console.log("add folder");
    const nf = await fileSystem.createFolder();
    await node.link("new folder", nf.id);
    node = node;
    collapsed = false;
  }

  async function addFile() {
    console.log("add file");
    const book = newBook("not visited", "add-in-folder-", "standard", null);
    await newFile(fileSystem, node, getCurrentDateTime(), book);
    node = node;
    collapsed = false;
  }

  async function removeFolder() {
    if ($selectedEntries.size > 1 && trash) {
      await removeSelectedEntries(fileSystem, trash);
      node = node;
    } else {
      dispatch('remove', bindId);
    }
  }

  async function removeChild(e: CustomEvent<BindId>) {
    console.log("remove child", e.detail, trash);
    const childBindId = e.detail as BindId;
    const childEntry = (await node.getEntry(childBindId))!;
    if (trash) {
      console.log("trash link", trash.id);
      await trash.link(childEntry[1], childEntry[2]);
    }
    await node.unlink(childBindId);
    node = node;
  }

  function onInsertToParent(ev: CustomEvent<DataTransfer>) {
    console.log("insert to parent", ev.detail);
    isDraggingOver = false;
    const detail = { dataTransfer: ev.detail, index };
    dispatch('insert', detail);
    ev.preventDefault();
    ev.stopPropagation();
    $fileManagerDragging = null;
  }

  function onDragStart(ev: DragEvent) {
    console.log("folder drag start", bindId, parent.id, renaming);
    if (renaming || removability === "unremovable") {
      ev.preventDefault();
      return;
    }
    ev.dataTransfer!.setData("bindId", bindId);
    ev.dataTransfer!.setData("parent", parent.id);
    ev.stopPropagation();
    setTimeout(() => {
      // こうしないとなぜかdragendが即時発火してしまう
      $fileManagerDragging = buildDragging(fileSystem, node.id, bindId, parent.id);
    }, 0);
  }

  async function onInsert(e: CustomEvent<{ dataTransfer: DataTransfer, index: number }>) {
    console.log("insert", e.detail);
    await moveToHere(e.detail.index);
  }

  async function onCollapsedInsert(e: CustomEvent<{ dataTransfer: DataTransfer, index: number }>) {
    console.log("collapsed insert", e.detail);
    await moveToHere(e.detail.index);
    collapsed = false;
  }

  function onFolderDropZoneDragOver(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    folderDropZoneDraggingOver = true;
  }

  function onFolderDropZoneDragLeave() {
    folderDropZoneDraggingOver = false;
  }

  async function onFolderDropZoneDrop(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    folderDropZoneDraggingOver = false;
    if (acceptable) {
      await moveToHere(null);
      collapsed = false;
    }
    $fileManagerDragging = null;
  }

  async function moveToHere(index: number | null) {
    const dragging = $fileManagerDragging!;
    console.log("++++++++++++ moveToHere", dragging, index);

    if (index === null) {
      index = (await node.list()).length;
    }

    for (const entry of dragging.entries) {
      const sourceParent = (await dragging.fileSystem.getNode(entry.parent)) as Folder;
      const mover = await sourceParent.getEntry(entry.bindId);
      if (!mover) {
        console.warn("entry not found, skipping", entry.bindId);
        continue;
      }

      if (fileSystem.id === dragging.fileSystem.id) {
        console.log("same filesystem move", node.id, "target index =", index);

        await sourceParent.unlink(entry.bindId);
        await node.insert(mover[1], mover[2], index);
        index++;
        console.log("insert done");
      } else {
        console.log("different filesystem move (is copy)", node.id, "target index =", index);

        $loading = true;
        const sourceNodeId = mover[2];
        const targetFileId = await copyBookOrFolderInterFileSystem(dragging.fileSystem, fileSystem, sourceNodeId);
        await node.insert(mover[1], targetFileId, index);
        index++;
        $loading = false;
      }
    }
    $selectedEntries = new Set();
  }

  async function recycle() {
    const editingId = $mainBook!.revision.id as NodeId;
    await recycleNode(node, editingId);
    if (!fileSystem.isVault) {
      $loading = true;
      const { usedImageFiles, strayImageFiles } = await collectGarbage(fileSystem);
      console.log("usedImageFiles", usedImageFiles);
      console.log("strayImageFiles", strayImageFiles);
      const imageFolder = (await (await fileSystem.getRoot()).getNodeByName("画像"))!.asFolder()!;
      await purgeCollectedGarbage(fileSystem, imageFolder, strayImageFiles);
      $loading = false;
    }
    $fileManagerUsedSizeToken = fileSystem;
    node = node;
  }

  // TODO: だいぶ遅い
  async function recycleNode(curr: Node, editingId: NodeId) {
    if (curr.getType() === "folder") {
      const folder = curr.asFolder()!;
      const entries = await folder.listEmbodied();
      for (const entry of entries) {
        if (entry[2].id === editingId) { continue; } // 編集中のファイルは消さない
        if (curr.id === node.id) { // node以外はどうせ親を消すのでunlink不要
          await folder.unlink(entry[0]);
        }
        await recycleNode(entry[2], editingId);
      }
    }
    if (curr.id !== node.id) {
      await fileSystem.destroyNode(curr.id);
    }
  }

  async function renameChild(e: CustomEvent<{ bindId: BindId, name: string }>) {
    console.log("rename child", e.detail);
    const { bindId, name } = e.detail;
    await node.rename(bindId, name);
    node = node;

    const childNode = (await node.getEmbodiedEntry(bindId))![2];
    if ($mainBook?.revision.id === childNode.id) {
      $mainBookTitle = name;
    }
  }

  async function duplicateChild(e: CustomEvent<{ bindId: BindId, nodeId: NodeId, filename: string }>) {
    console.log("duplicate child", e.detail);
    const { bindId, nodeId, filename } = e.detail;
    
    try {
      $loading = true;
      
      // 元のファイルを読み込み
      const sourceFile = (await fileSystem.getNode(nodeId))!.asFile()!;
      const book = await loadBookFrom(fileSystem, sourceFile);
      
      // 新しいファイルを作成
      const newFile = await fileSystem.createFile();
      book.revision.id = newFile.id;
      
      // 保存
      await saveBookTo(book, fileSystem, newFile);
      
      // 複製したファイル名を生成
      const duplicatedName = `${filename} - ${getCurrentDateTime()}`;
      
      // フォルダにリンク
      await node.link(duplicatedName, newFile.id);
      
      // UIを更新
      node = node;
      
      toastStore.trigger({ message: $_('fileManager.duplicated'), timeout: 2000});
    } catch (error) {
      console.error("複製中にエラーが発生しました:", error);
      toastStore.trigger({ message: $_('fileManager.duplicateFailed'), timeout: 3000});
    } finally {
      $loading = false;
    }
  }

  async function startRename() {
    if ($selectedEntries.size > 1) {
      const result = await waitDialog<BulkRenameResult>('bulkRename', { count: $selectedEntries.size });
      await renameSelectedEntries(fileSystem, result);
      node = node;
      return;
    }
    console.log("renameFolder");
    renameEdit.setFocus();
  }

  function submitRename(e: CustomEvent<string>) {
    console.log("submitRename", e.detail);
    dispatch('rename', { bindId, name: e.detail });
    renaming = false;
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'F2' && node && $lastSelectedEntry === node.id && isDiscardable && !renaming) {
      e.preventDefault();
      startRename();
    }
  }

  const watcher = {
    onInsert: (bindId: BindId, index: number, sourceParent: Folder | null) => {
      console.log("onInsert", bindId, index, sourceParent);
      node = node;
    },
    onDelete: (bindId: BindId) => {
      console.log("onDelete", bindId);
      node = node;
    },
    onRename: (bindId: BindId, newName: string) => {
      console.log("onRename", bindId, newName);
      node = node;
    }
  }
  let watching = false;

  onMount(async () => {
    const root = await fileSystem.getRoot();
    isRootTrash = trash == null && parent.id === root.id;

    const entry: [BindId, string, Node] = (await parent.getEmbodiedEntry(bindId))!
    node = entry[2] as Folder;
    if (folderCollapsedState.has(node.id)) {
      collapsed = folderCollapsedState.get(node.id)!;
    }
    isDiscardable = removability === "removable" && trash != null;
    node.watch(watcher);
    watching = true;
  });

  onDestroy(() => {
    if (watching) {
      node.unwatch(watcher);
    }
  });

  /**
   * フォルダ内の全ファイルのページを1冊のbookにまとめる
   */
  async function mergeFilesIntoOne() {
    const entries = await node.listEmbodied();
    const fileEntries = entries.filter(([, , n]) => n.getType() === 'file');

    if (fileEntries.length === 0) {
      toastStore.trigger({ message: $_('fileManager.noFilesToMerge'), timeout: 2000 });
      return;
    }

    const confirmed = await waitDialog<boolean>('confirm', {
      title: $_('fileManager.mergeFiles'),
      message: $_('fileManager.mergeFilesConfirm', { values: { count: fileEntries.length } }),
      positiveButtonText: $_('fileManager.mergeFilesExecute'),
      negativeButtonText: $_('fileManager.mergeFilesCancel'),
    });
    if (!confirmed) { return; }

    try {
      $loading = true;
      $progress = 0;

      const allPages: import('../lib/book/book').Page[] = [];
      let direction: import('../lib/book/book').ReadingDirection = 'right-to-left';
      let wrapMode: import('../lib/book/book').WrapMode = 'two-pages';

      for (let i = 0; i < fileEntries.length; i++) {
        const [, , childNode] = fileEntries[i];
        const file = childNode.asFile()!;
        const book = await loadBookFrom(fileSystem, file);
        allPages.push(...book.pages);
        if (i === 0) {
          direction = book.direction;
          wrapMode = book.wrapMode;
        }
        $progress = (i + 1) / fileEntries.length;
      }

      const mergedBook = newBook("not visited", "add-in-folder-", "standard", null);
      mergedBook.pages = allPages;
      mergedBook.direction = direction;
      mergedBook.wrapMode = wrapMode;

      await newFile(fileSystem, node, `${filename} - ${$_('fileManager.merged')}`, mergedBook);
      node = node;
      collapsed = false;

      toastStore.trigger({ message: $_('fileManager.mergeFilesCompleted', { values: { count: allPages.length } }), timeout: 2000 });
    } catch (error) {
      console.error('ファイル結合中にエラーが発生しました:', error);
      toastStore.trigger({ message: $_('fileManager.mergeFilesFailed'), timeout: 3000 });
    } finally {
      $progress = null;
      $loading = false;
    }
  }

  /**
   * ファイル名をサニタイズする
   * ZIPファイル名に使えない文字を置き換える
   */
  function sanitizeFileName(name: string): string {
    return name
      .replace(/[\/:*?"<>|\\]/g, '_') // Windows/ZIPで無効な文字を置き換え
      .replace(/\s+/g, ' ')           // 連続した空白を1つに
      .trim();                         // 先頭と末尾の空白を削除
  }

  /**
   * フォルダをZIPファイルとしてエクスポートする
   */
  async function exportAsZip() {
    try {
      // プログレスバーの表示
      $loading = true;
      $progress = 0;

      let zipBlob: Blob;
      let downloadFilename: string;

      if ($selectedEntries.size > 1) {
        zipBlob = (await exportSelectedEntries(fileSystem, (value) => { $progress = value; }))!;
        const dateStr = new Date().toISOString().replace(/[-:]/g, '').substring(0, 15);
        downloadFilename = `selected_${dateStr}.zip`;
      } else {
        zipBlob = await exportFolderAsEnvelopeZip(
          fileSystem,
          node,
          filename,
          (value) => { $progress = value; }
        );
        const safeFilename = sanitizeFileName(filename || 'folder');
        const dateStr = new Date().toISOString().replace(/[-:]/g, '').substring(0, 15);
        downloadFilename = `${safeFilename}_${dateStr}.zip`;
      }
      
      // ダウンロードリンクの作成
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      
      // クリックでダウンロード開始
      a.click();
      
      // クリーンアップ
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // 成功メッセージ
      toastStore.trigger({ message: $_('fileManager.exportedAsZip'), timeout: 2000});
    } catch (error) {
      console.error('エクスポート中にエラーが発生しました:', error);
      toastStore.trigger({ message: $_('fileManager.exportError'), timeout: 3000});
    } finally {
      // プログレスバーの非表示
      $progress = null;
      $loading = false;
    }
  }
</script>

<svelte:window on:keydown={onKeyDown}/>
{#if node}
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="folder"
  class:folder-highlight={folderDropZoneDraggingOver || (isDraggingOver && acceptable)}
  on:dragover={onDragOver}
  on:dragstart={onDragStart}
  on:dragleave={onDragLeave}
  on:drop={onDropHere}
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    class="folder-title-line"
    class:no-select={removability === "unremovable"}
    draggable={removability === "removable"}
    on:click={(e) => { handleEntryClick(node.id, e); }}
    data-node-id={node.id}
    data-bind-id={bindId}
    data-parent-id={parent.id}
  >
    <div class="folder-title">
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
      <div class="foldername" use:toolTip={$_('fileManager.moveByDrag')} on:click|stopPropagation={(e) => { if (!renaming) { collapsed = !collapsed; handleEntryClick(node.id, e); } }}>
        <img class="button toggle" class:collapsed={collapsed} src={folderIcon} alt="symbol"/>
        <RenameEdit bind:this={renameEdit} bind:editing={renaming} value={filename} on:submit={submitRename}/>
      </div>
      {#if isRootTrash}
        <button class="btn btn-sm variant-filled recycle-button px-1 py-0" on:click={recycle}>{$_('storage.emptyTrash')}</button>
      {/if}
      <div class="button-container">
        {#if spawnability === "file-spawnable"}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
          <img class="button" src={newFileIcon} alt="new file" on:click={addFile} use:toolTip={$_('fileManager.createPage')}/>
        {/if}
        {#if spawnability === "folder-spawnable"}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
          <img class="button" src={newFolderIcon} alt="new folder" on:click={addFolder} use:toolTip={$_('fileManager.createFolder')}/>
        {/if}
      </div> 
    </div>
    {#if $lastSelectedEntry === node.id && isDiscardable}
      <div class="floating-actions">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <img class="action-icon" src={mergeIcon} alt="merge" on:click={mergeFilesIntoOne} use:toolTip={$_('fileManager.mergeFiles')}/>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <img class="action-icon" src={packageExportIcon} alt="archive" on:click={exportAsZip} use:toolTip={$_('storage.archiveToZip')}/>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <img class="action-icon" src={renameIcon} alt="rename" on:click={startRename} use:toolTip={$_('fileManager.changeFolderName')}/>
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
        <img class="action-icon" src={trashIcon} alt="trash" on:click={removeFolder} use:toolTip={$_('fileManager.discard')}/>
      </div>
    {/if}
    {#if removability === 'removable'}
      <FileManagerInsertZone on:drop={onInsertToParent} bind:acceptable={acceptable} depth={path.length}/>
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        class="folder-drop-zone"
        class:acceptable={acceptable}
        class:folder-dragging-over={folderDropZoneDraggingOver}
        on:dragover={onFolderDropZoneDragOver}
        on:dragleave={onFolderDropZoneDragLeave}
        on:drop={onFolderDropZoneDrop}
        style="z-index: {path.length + 1}"
      ></div>
    {/if}
  </div>
  {#if collapsed}
  <div class="folder-contents">
    <FileManagerFolderTail index={embodiedEntries.length} on:insert={onCollapsedInsert} path={[...path, 'tail']}/>
  </div>
  {:else}
  <div class="folder-contents">
    {#each embodiedEntries as [bindId, filename, childNode], index (childNode.id)}
      {#if childNode.getType() === 'folder'}
        <svelte:self fileSystem={fileSystem} removability={"removable"} spawnability={spawnability} filename={filename} bindId={bindId} parent={node} index={index} on:insert={onInsert} on:remove={removeChild} path={[...path, bindId]} on:rename={renameChild} trash={trash}/>
      {:else if childNode.getType() === 'file'}
        <FileManagerFile fileSystem={fileSystem} removability={"removable"} nodeId={childNode.id} filename={filename} bindId={bindId} parent={node} index={index} on:insert={onInsert} path={[...path, bindId]} on:remove={removeChild} on:rename={renameChild} on:duplicate={duplicateChild} trash={trash}/>
      {/if}
    {/each}
    <FileManagerFolderTail index={embodiedEntries.length} on:insert={onInsert} path={[...path, 'tail']}/>
  </div>
  {/if}
</div>
{/if}

<style>
  .folder {
    text-align: left;
  }
  .folder.folder-highlight {
    background-color: #ee84;
    box-shadow: 0 0 0 2px #444 inset;
  }
  .folder-title-line {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 24px;
    margin-right: 2px;
    position: relative; /* InsertZone用 */
  }
  .folder-title-line:hover {
    background-color: #fff4;
  }
  .folder-title {
    font-size: 16px;
    font-weight: 700;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-family: 'Zen Maru Gothic';
    font-weight: 700;
    font-style: normal;
    width: 100%;
    height: 20px;
    margin-left: 4px;
  }
  .foldername {
    background: rgb(221, 235, 189);
    border-radius: 8px;
    padding-left: 4px;
    padding-right: 4px;
    padding-top: 2px;
    padding-bottom: 2px;
    display: flex;
    flex-direction: row;
    margin-right: 8px;
    cursor: pointer;
  }
  .folder-contents {
    padding-left: 16px;
    box-sizing: border-box;
    /* border: 2px dashed transparent; /* 初期状態では透明にしておく */
  }
  .floating-actions {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    padding: 2px 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    z-index: 1;
  }
  .action-icon {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
  .button {
    width: 16px;
    height: 16px;
    display: inline;
  }
  .toggle {
    cursor: pointer;
    transition: transform 0.15s ease;
  }
  .toggle.collapsed {
    transform: rotate(-90deg);
  }
  .folder-drop-zone {
    position: absolute;
    top: 33%;
    left: 0;
    right: 0;
    height: 34%;
    display: none;
  }
  .folder-drop-zone.acceptable {
    display: block;
  }
  .no-select {
    user-select: none; /* 標準のCSS */
    -webkit-user-select: none; /* Chrome, Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE/Edge */
  }
</style>
